import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { 
  FaSalesforce, FaCode, FaRobot, FaSync, FaShieldAlt, 
  FaChartPie, FaChevronDown, FaBolt, FaCloud, 
  FaDatabase, FaCheckCircle, FaProjectDiagram, FaServer 
} from "react-icons/fa";

import salesforceImg from "../assets/images/salesforce.jpg";
import "../styles/Salesforce.css";

const sfFaqs = [
  {
    q: "How do you optimize Apex performance for high-volume orgs?",
    a: "We focus on 'Bulkification'—ensuring all triggers and classes handle collections of records. We also utilize Asynchronous Apex (Batch, Queueable, and Future methods) to stay within Salesforce Governor Limits while processing millions of data rows."
  },
  {
    q: "Can you implement Salesforce Data Cloud and Agentforce?",
    a: "Yes. We specialize in the 'Einstein 1 Platform' architecture, integrating Data Cloud to unify customer touchpoints and deploying Agentforce (AI Agents) to automate complex customer service and sales actions."
  },
  {
    q: "What is your strategy for Salesforce DevOps?",
    a: "We utilize Salesforce DX (SFDX) combined with CI/CD pipelines (GitHub Actions/Copado). This ensures version-controlled development, automated regression testing, and seamless deployments across Scratch Orgs and Sandboxes."
  }
];

export default function Salesforce() {
  const [activeFaq, setActiveFaq] = useState(null);

  return (
    <div className="sf-page animated-bg">
      <Helmet>
        <title>Salesforce Architect & LWC Developer | Enterprise CRM Engineering</title>
        <meta name="description" content="Certified Salesforce Architects specializing in Apex, LWC, and AI-driven Agentforce. We optimize Sales Cloud, Service Cloud, and Data Cloud for global enterprises." />
        <meta name="keywords" content="Salesforce Consultant, LWC Developer, Apex Triggers, Salesforce Architect, Agentforce AI, Data Cloud Integration, SFDX DevOps" />
      </Helmet>

      <div className="animated-mesh"></div>

      {/* --- HERO SECTION --- */}
      <section className="sf-hero">
        <div className="ns-container">
          <div className="sf-hero-grid">
            <motion.div initial={{ opacity: 0, x: -50 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8 }}>
              <div className="badge-modern sf-badge"><FaSalesforce /> Salesforce Certified Experts</div>
              <h1>Architecting Global <br/> <span className="text-gradient-sf">Revenue Systems</span></h1>
              <p className="ns-description">
                ITSeva Hub transforms standard CRM into an <strong>AI-First Ecosystem</strong>. 
                From <strong>LWC high-performance UI</strong> to <strong>Agentforce automation</strong>, we engineer 
                Salesforce instances that drive measurable ROI.
              </p>
              <div className="ns-hero-btns">
                <Link to="/contact" className="ns-btn-primary sf-btn">Request Technical Audit</Link>
                <div className="hero-stat"><strong>200+</strong> <span>Apex Deployments</span></div>
              </div>
            </motion.div>

            <motion.div className="sf-hero-viz" initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }}>
              <div className="viz-glass">
                <img src={salesforceImg} alt="Salesforce Enterprise Architecture" className="sf-main-img" />
                <motion.div animate={{ y: [0, -10, 0] }} transition={{ duration: 3, repeat: Infinity }} className="floating-tag t1">Apex & LWC</motion.div>
                <motion.div animate={{ y: [0, 10, 0] }} transition={{ duration: 4, repeat: Infinity }} className="floating-tag t2">Agentforce AI</motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* --- STRATEGIC PILLARS --- */}
      <section className="sf-pillars">
        <div className="ns-container">
          <div className="pillars-grid">
            <div className="pillar-item glass">
              <FaBolt />
              <h3>Velocity</h3>
              <p>Lightning-fast UI performance using optimized LWC components and Aura migration.</p>
            </div>
            <div className="pillar-item glass">
              <FaRobot />
              <h3>Intelligence</h3>
              <p>Deploying Einstein GPT and autonomous AI Agents for proactive customer success.</p>
            </div>
            <div className="pillar-item glass">
              <FaSync />
              <h3>Connectivity</h3>
              <p>Unified data flow using Mulesoft and Data Cloud for a 360-degree customer view.</p>
            </div>
          </div>
        </div>
      </section>

      {/* --- CORE CAPABILITIES (BENTO) --- */}
      <section className="sf-services">
        <div className="ns-container">
          <div className="section-title center">
            <span className="accent-text">Capabilities</span>
            <h2>Salesforce Engineering Layers</h2>
          </div>

          <div className="bento-grid">
            <motion.div whileHover={{ y: -5 }} className="bento-card glass tall">
              <FaCode className="b-icon sf-blue" />
              <h3>Apex & LWC Dev</h3>
              <p>We build complex <strong>Lightning Web Components</strong> and bulkified <strong>Apex Triggers</strong> that respect Salesforce Governor Limits.</p>
              <ul className="bento-list">
                <li><FaCheckCircle /> Asynchronous Processing</li>
                <li><FaCheckCircle /> Dynamic UI Components</li>
                <li><FaCheckCircle /> Performance Tuning</li>
              </ul>
            </motion.div>

            <div className="bento-right-stack">
              <motion.div whileHover={{ y: -5 }} className="bento-card glass wide">
                <FaRobot className="b-icon sf-purple" />
                <div>
                  <h3>AI & Agentforce Implementation</h3>
                  <p>Harnessing the power of <strong>Salesforce Einstein</strong> to build autonomous agents that handle case resolutions and lead scoring without human intervention.</p>
                </div>
              </motion.div>

              <div className="bento-inner-grid">
                <motion.div whileHover={{ y: -5 }} className="bento-card glass">
                  <FaSync className="b-icon sf-indigo" />
                  <h3>Cloud Sync</h3>
                  <p>Mulesoft & REST API integrations with NetSuite and ERPs.</p>
                </motion.div>
                <motion.div whileHover={{ y: -5 }} className="bento-card glass">
                  <FaBolt className="b-icon sf-yellow" />
                  <h3>Flow Strategy</h3>
                  <p>Complex declarative automation using Advanced Flows.</p>
                </motion.div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* --- TECHNICAL DEEP DIVE (SEO BLOCK) --- */}
      <section className="sf-deep-dive">
        <div className="ns-container">
          <div className="deep-dive-grid glass">
            <div className="dive-text">
              <h2>Enterprise CRM Governance</h2>
              <p>
                A high-performing Salesforce instance requires more than just configuration. Our <strong>Salesforce Architects</strong> 
                delve into the <strong>multi-tenant architecture</strong> to optimize CPU time and heap size. 
              </p>
              <p>
                By utilizing <strong>Salesforce Data Cloud</strong>, we ingest trillions of records from external streams, 
                harmonizing them into a single DMO (Data Model Object). This allows your <strong>Sales Cloud</strong> and 
                <strong> Service Cloud</strong> instances to act on real-time data intelligence.
              </p>
              <div className="tech-tags">
                <span>Salesforce DX</span> <span>OmniStudio</span> <span>CPQ</span> <span>Vlocity</span> <span>AppExchange</span>
              </div>
            </div>
            <div className="dive-stats">
              <div className="d-stat"><h4>40%</h4><p>Faster Deployments</p></div>
              <div className="d-stat"><h4>0</h4><p>Governor Limit Errors</p></div>
              <div className="d-stat"><h4>24/7</h4><p>Cloud Monitoring</p></div>
            </div>
          </div>
        </div>
      </section>

      {/* --- METHODOLOGY --- */}
      <section className="sf-lifecycle">
        <div className="ns-container">
          <div className="section-title center">
            <span className="accent-text">The Process</span>
            <h2>Our Architectural Roadmap</h2>
          </div>
          <div className="lifecycle-grid">
            {[
              { icon: <FaProjectDiagram />, title: "Discovery", desc: "Object modeling, security review, and process mapping." },
              { icon: <FaServer />, title: "Architecture", desc: "Technical Design Document (TDD) and ERD finalization." },
              { icon: <FaCode />, title: "Engineering", desc: "Test-driven development (Apex) and LWC UI construction." },
              { icon: <FaShieldAlt />, title: "Deployment", desc: "CI/CD automated release using SFDX and GitHub." }
            ].map((step, idx) => (
              <div className="step-card" key={idx}>
                <div className="step-num">{idx + 1}</div>
                {step.icon}
                <h4>{step.title}</h4>
                <p>{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* --- FAQ --- */}
      <section className="sf-faq">
        <div className="ns-container narrow">
          <h2 className="center">Salesforce Insights & FAQ</h2>
          <div className="faq-list">
            {sfFaqs.map((faq, index) => (
              <div className={`faq-item glass ${activeFaq === index ? "active" : ""}`} key={index}>
                <button onClick={() => setActiveFaq(activeFaq === index ? null : index)}>
                  {faq.q} <FaChevronDown className={activeFaq === index ? "rotate" : ""} />
                </button>
                <AnimatePresence>
                  {activeFaq === index && (
                    <motion.div className="faq-answer" initial={{ height: 0 }} animate={{ height: "auto" }} exit={{ height: 0 }}>
                      <p>{faq.a}</p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </div>
        </div>
      </section>

      <footer className="sf-cta">
        <div className="ns-container center">
          <div className="cta-glass">
            <h2>Transform Your Business with Salesforce</h2>
            <p>Consult with our architects to optimize your Einstein 1 Platform.</p>
            <Link to="/contact" className="btn-main-glow sf-glow">Talk to an Architect</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}